import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, getProgressTable, getLessonsTable } from '../utils/supabase';
import { type LanguageLevel } from '../types';
import { useTranslation } from '../utils/translations';

interface ContinueLastLessonPromptProps {
  userId: string;
  level: LanguageLevel;
  onClose: () => void;
  nativeLanguage?: string;
}

export function ContinueLastLessonPrompt({ userId, level, onClose, nativeLanguage }: ContinueLastLessonPromptProps) {
  const [loading, setLoading] = useState(true);
  const [lessonTitle, setLessonTitle] = useState<string | null>(null);
  const [lessonId, setLessonId] = useState<string | null>(null);
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const { t } = useTranslation(nativeLanguage);

  useEffect(() => {
    mountedRef.current = true;
    
    const findLastUnlockedLesson = async () => {
      try {
        setLoading(true);
        
        // Get the progress table based on user's native language
        const progressTable = getProgressTable();
        const lessonsTable = getLessonsTable();
        
        // First get all completed lessons for this user and level
        const { data: completedLessons, error: completedError } = await supabase
          .from(progressTable)
          .select('lesson_id, completed')
          .eq('user_id', userId)
          .eq('completed', true);
          
        if (completedError) {
          console.error('Error fetching completed lessons:', completedError);
          if (mountedRef.current) {
            setLoading(false);
          }
          onClose();
          return;
        }
        
        if (!completedLessons || completedLessons.length === 0) {
          console.log('No completed lessons found, finding first lesson');

          // No completed lessons, find the first lesson for this level
          // Only show public lessons or lessons generated for this user
          const { data: firstLesson, error: firstLessonError } = await supabase
            .from(lessonsTable)
            .select('id, title')
            .eq('level', level)
            .eq('is_review', false)
            .or(`generated_for.is.null,generated_for.eq.${userId}`)
            .order('order_index', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (firstLessonError) {
            console.error('Error fetching first lesson:', firstLessonError);
            if (mountedRef.current) {
              setLoading(false);
            }
            onClose();
            return;
          }

          if (!firstLesson) {
            if (mountedRef.current) {
              setLoading(false);
            }
            onClose();
            return;
          }
          
          if (mountedRef.current) {
            setLessonId(firstLesson.id);
            setLessonTitle(firstLesson.title);
            setLoading(false);
          }
          return;
        }
        
        // Get the last completed lesson IDs
        const completedLessonIds = completedLessons.map(l => l.lesson_id);
        
        // Get details of completed lessons
        // Only show public lessons or lessons generated for this user
        const { data: lastCompletedLessons, error: lastLessonError } = await supabase
          .from(lessonsTable)
          .select('id, title, order_index, level')
          .in('id', completedLessonIds)
          .eq('level', level)
          .or(`generated_for.is.null,generated_for.eq.${userId}`)
          .order('order_index', { ascending: false });

        if (lastLessonError) {
          console.error('Error fetching completed lesson details:', lastLessonError);
          if (mountedRef.current) {
            setLoading(false);
          }
          onClose();
          return;
        }

        if (!lastCompletedLessons || lastCompletedLessons.length === 0) {
          console.log('No completed lessons found for this level');
          // Find the first lesson for this level
          // Only show public lessons or lessons generated for this user
          const { data: firstLesson, error: firstLessonError } = await supabase
            .from(lessonsTable)
            .select('id, title')
            .eq('level', level)
            .eq('is_review', false)
            .or(`generated_for.is.null,generated_for.eq.${userId}`)
            .order('order_index', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (firstLessonError) {
            console.error('Error fetching first lesson:', firstLessonError);
            if (mountedRef.current) {
              setLoading(false);
            }
            onClose();
            return;
          }

          if (!firstLesson) {
            if (mountedRef.current) {
              setLoading(false);
            }
            onClose();
            return;
          }
          
          if (mountedRef.current) {
            setLessonId(firstLesson.id);
            setLessonTitle(firstLesson.title);
            setLoading(false);
          }
          return;
        }

        // Get the last completed lesson
        const lastCompletedLesson = lastCompletedLessons[0];

        // Find the next lesson after the last completed one
        // Only show public lessons or lessons generated for this user
        const { data: nextLesson, error: nextLessonError } = await supabase
          .from(lessonsTable)
          .select('id, title')
          .eq('level', level)
          .eq('is_review', false)
          .or(`generated_for.is.null,generated_for.eq.${userId}`)
          .gt('order_index', lastCompletedLesson.order_index)
          .order('order_index', { ascending: true })
          .limit(1)
          .maybeSingle();
          
        if (nextLessonError) {
          console.error('Error fetching next lesson:', nextLessonError);
          // Use the last completed lesson as fallback
          if (mountedRef.current) {
            setLessonId(lastCompletedLesson.id);
            setLessonTitle(lastCompletedLesson.title);
            setLoading(false);
          }
          return;
        }
        
        if (nextLesson) {
          // Found a next lesson
          if (mountedRef.current) {
            setLessonId(nextLesson.id);
            setLessonTitle(nextLesson.title);
          }
        } else {
          // No next lesson, use the last completed one
          if (mountedRef.current) {
            setLessonId(lastCompletedLesson.id);
            setLessonTitle(lastCompletedLesson.title);
          }
        }
        
        if (mountedRef.current) {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error finding last unlocked lesson:', error);
        if (mountedRef.current) {
          setLoading(false);
        }
        onClose();
      }
    };
    
    findLastUnlockedLesson();
    
    return () => {
      mountedRef.current = false;
    };
  }, [userId, level, onClose]);
  
  const handleContinue = () => {
    if (lessonId) {
      navigate(`/lesson/${lessonId}`);
    }
    onClose();
  };
  
  const handleCancel = () => {
    onClose();
  };
  
  if (loading || !lessonId || !lessonTitle) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('Continue Learning')}</h2>

        <p className="text-gray-600 mb-6">
          {t('Would you like to continue with your last unlocked lesson?')}
        </p>
        
        <div className="bg-indigo-50 p-4 rounded-lg mb-6">
          <p className="font-medium text-indigo-800">{lessonTitle}</p>
          <p className="text-sm text-indigo-600">Level: {level}</p>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            {t('Not Now')}
          </button>

          <button
            onClick={handleContinue}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            {t('Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}